/**
 * Types du pilotage : ce que le patron regarde le matin, sur un seul écran.
 *
 * Ce domaine ne possède rien. Il assemble — trésorerie, encours, rentabilité,
 * charge — et c'est sa raison d'être : chacun de ces chiffres existait déjà,
 * mais éparpillé sur quatre écrans qu'il fallait ouvrir l'un après l'autre.
 * Le rassembler ne crée pas une donnée de plus, il supprime trois clics.
 *
 * Tout ce fichier est pur, donc éprouvable sans base.
 */
import type { Employee } from "../team/types.js";
import { employeeDisplayName } from "../team/types.js";
import type { Assignment } from "../field/types.js";
import { halvesCoveredOn } from "../field/types.js";
import { isActiveOn } from "../field/types.js";
import type { Absence } from "../planning/types.js";
import { blocksPlanning } from "../planning/types.js";
import { startOfWeek, weekDates } from "../team/types.js";
import { round2 } from "../../money.js";

/** Une ligne du tableau « rentabilité par chantier ». */
export interface ChantierProfitability {
  projectId: string;
  projectName: string;
  clientName: string | null;
  /** Moyenne des phases. `null` quand aucune phase n'est saisie. */
  progressPercent: number | null;
  /** Heures imputées au chantier, telles que le pointage les a constatées. */
  hoursWorked: number;
  /**
   * La marge annoncée au moment du devis. `null` quand le chantier n'est
   * rattaché à aucun devis — ça arrive, un chantier peut naître d'un accord
   * verbal.
   */
  quotedMarginPercent: number | null;
  /** La marge constatée. `null` tant que rien n'est facturé. */
  realMarginPercent: number | null;
}

/**
 * L'écart entre ce qu'on avait promis et ce qu'on constate.
 *
 * Rendu `null` plutôt que zéro quand l'un des deux manque : un chantier sans
 * devis afficherait sinon « 0 pt d'écart », ce qui se lit « conforme » alors
 * que ça veut dire « on ne sait pas ».
 */
export function marginGap(row: ChantierProfitability): number | null {
  if (row.quotedMarginPercent == null || row.realMarginPercent == null) return null;
  return Math.round((row.realMarginPercent - row.quotedMarginPercent) * 10) / 10;
}

/** Un chantier qui rend moins que promis, d'au moins un point. */
export function isUnderQuoted(row: ChantierProfitability): boolean {
  const gap = marginGap(row);
  return gap != null && gap <= -1;
}

/** La charge d'une semaine, capacité contre engagement. */
export interface WeekLoad {
  /** Lundi, AAAA-MM-JJ. */
  weekStart: string;
  /** Numéro de semaine ISO — « S 16 », comme on le dit sur un chantier. */
  weekNumber: number;
  /** Somme des durées contractuelles des salariés actifs. */
  capacityHours: number;
  /** Ce que les affectations du planning représentent. Voir `computeWeekLoad`. */
  engagedHours: number;
  /** Engagé sur capacité, en pourcentage. `null` sans capacité connue. */
  percent: number | null;
}

/** Le carnet de commandes : ce qui est signé et pas encore commencé. */
export interface Backlog {
  signedHt: number;
  quoteCount: number;
  /**
   * Converti en semaines de travail. `null` quand aucun devis retenu ne porte
   * de durée estimée — auquel cas on affiche le montant seul plutôt qu'un
   * nombre de semaines inventé.
   */
  weeks: number | null;
}

export interface Pilotage {
  /** Le mois projeté le plus proche. `null` si la trésorerie ne projette rien. */
  cash: {
    month: string;
    income: number;
    expense: number;
    net: number;
    balance: number;
  } | null;
  receivablesTotal: number;
  receivablesOverdue: number;
  receivablesOverdueCount: number;
  /** Marge moyenne des devis du mois, en points. */
  marginPercent: number | null;
  backlog: Backlog;
  chantiers: ChantierProfitability[];
  load: WeekLoad[];
}

const DAY_MS = 86_400_000;

/**
 * Le numéro de semaine ISO 8601.
 *
 * La règle est celle qu'un conducteur de travaux utilise sans le savoir : la
 * semaine 1 est celle qui contient le premier jeudi de l'année. Écrite ici
 * plutôt qu'approchée par « jour de l'année divisé par sept », qui se trompe
 * une année sur deux au tournant de janvier — et un planning qui annonce la
 * mauvaise semaine en janvier est un planning qu'on cesse de croire.
 */
export function isoWeekNumber(isoDate: string): number {
  const t = Date.parse(`${isoDate.slice(0, 10)}T00:00:00Z`);
  const d = new Date(t);
  // On se place sur le jeudi de la semaine courante : c'est lui qui décide de
  // l'année ISO à laquelle la semaine appartient.
  const jeudi = new Date(t + (3 - ((d.getUTCDay() + 6) % 7)) * DAY_MS);
  const premierJanvier = Date.parse(`${jeudi.getUTCFullYear()}-01-01T00:00:00Z`);
  return Math.floor((jeudi.getTime() - premierJanvier) / DAY_MS / 7) + 1;
}

/** Les jours ouvrés d'une semaine : du lundi au vendredi. */
const OUVRES = 5;

/**
 * La charge d'une semaine, déduite du planning.
 *
 * **Aucune heure n'est saisie pour cela**, et c'est le point : l'entreprise
 * n'estime pas le volume de chaque chantier, personne ne le lui a demandé, et
 * inventer une saisie de plus serait le meilleur moyen qu'elle ne soit jamais
 * faite. On lit donc ce qui existe — qui est affecté, où, quels jours — et on
 * le convertit au prorata de la durée contractuelle : un salarié à 35 h affecté
 * du lundi au vendredi engage 35 h ; affecté deux jours, il en engage 14.
 *
 * Ce que ce chiffre **ne dit pas**, et qu'il ne faut pas lui faire dire : il
 * mesure la couverture du planning, pas le travail qu'exige l'ouvrage. Une
 * semaine à 100 % n'est pas une semaine pleine, c'est une semaine où tout le
 * monde est placé.
 *
 * Le week-end ne compte pas. Une affectation qui court sur un chantier long
 * couvre le samedi et le dimanche par construction — les compter ferait
 * dépasser 100 % toutes les semaines, et une jauge toujours au maximum
 * n'informe plus.
 */
export function computeWeekLoad(
  weekOf: string,
  employees: readonly Employee[],
  assignments: readonly Assignment[],
  absences: readonly Absence[] = [],
): WeekLoad {
  const weekStart = startOfWeek(weekOf);
  const jours = weekDates(weekStart).slice(0, OUVRES);

  const actifs = employees.filter((e) => e.isActive);

  let capacityHours = 0;
  let engagedHours = 0;
  for (const employee of actifs) {
    // Les jours où la personne est là. Une absence accordée sort du calcul
    // des deux côtés : elle ne fournit pas de capacité, et elle ne remplit
    // pas le planning. La compter dans la capacité seule faisait annoncer la
    // fermeture d'août comme trois semaines en creux et la capacité entière
    // « à vendre » — au moment précis où il n'y avait personne à vendre.
    const presents = jours.filter(
      (d) =>
        !absences.some(
          (ab) => ab.employeeId === employee.id && blocksPlanning(ab) && isActiveOn(ab, d),
        ),
    );
    capacityHours += (employee.weeklyHours / OUVRES) * presents.length;

    const siennes = assignments.filter((a) => a.employeeId === employee.id);
    // Compté en demi-journées : un matin engage la moitié d'une journée. Une
    // demi-journée compte une fois, même si deux chantiers s'y superposent —
    // la personne n'est pas disponible deux fois ; et le matin sur un chantier
    // plus l'après-midi sur un autre font une journée, pas deux.
    const demiJournees = presents.reduce(
      (n, d) => n + new Set(siennes.flatMap((a) => halvesCoveredOn(a, d))).size,
      0,
    );
    engagedHours += (employee.weeklyHours / OUVRES) * (demiJournees / 2);
  }

  const arrondi = (n: number) => Math.round(n * 10) / 10;

  return {
    weekStart,
    weekNumber: isoWeekNumber(weekStart),
    capacityHours: arrondi(capacityHours),
    engagedHours: arrondi(engagedHours),
    percent: capacityHours > 0 ? Math.round((engagedHours / capacityHours) * 100) : null,
  };
}

/**
 * Le trou à vendre : ce qui manque pour remplir les semaines regardées.
 *
 * Rendu à zéro plutôt qu'en négatif quand tout est plein — « −12 h à vendre »
 * ne veut rien dire.
 */
export function hoursToSell(load: readonly WeekLoad[]): number {
  const manque = load.reduce((s, w) => s + Math.max(0, w.capacityHours - w.engagedHours), 0);
  return Math.round(manque * 10) / 10;
}

/**
 * Le nombre de points d'attention, tel que l'en-tête l'annonce.
 *
 * Trois natures seulement, et chacune appelle un geste le jour même : une
 * facture échue à relancer, un chantier qui rend moins que promis, et — quand
 * la charge est connue — une semaine à venir sous la moitié de la capacité.
 * Un compteur qui agrégerait tout ce qui cloche finirait à quarante et ne
 * ferait plus lever personne.
 */
export function attentionCount(pilotage: Pilotage): number {
  const derives = pilotage.chantiers.filter(isUnderQuoted).length;
  const creux = pilotage.load.filter((w) => w.percent != null && w.percent < 50).length;
  return (pilotage.receivablesOverdueCount > 0 ? 1 : 0) + derives + creux;
}

/* ─── Ce que chacun coûte, et ce qu'il rapporte ─────────────────────────── */

/**
 * Une ligne du tableau « ce que chacun coûte et rapporte ».
 *
 * Le patron l'a formulé ainsi : « sur un chantier à 10 000 €, un employé a
 * travaillé 35 h pour faire un parquet ; on retire la main-d'œuvre chargée et
 * les matériaux, et on obtient ce qu'il a rapporté ». C'est exactement la marge
 * du chantier — et c'est ce qui rend le calcul honnête plutôt qu'inventé :
 * `production − matières − coût` **est** la marge que la fiche chantier affiche
 * déjà. On ne produit pas un second chiffre, on répartit le premier.
 *
 * La répartition se fait au prorata des heures imputables, avec une nuance qui
 * compte : chacun est **débité de son propre coût**, pas d'une quote-part du
 * coût moyen. Un compagnon cher et un apprenti qui font les mêmes heures
 * touchent la même production et ne rendent pas la même contribution — ce qui
 * est précisément ce que le patron cherche à voir.
 */
export interface EmployeeProfitability {
  employeeId: string;
  employeeName: string;
  isActive: boolean;

  /** Toutes les heures pointées : travaillées, trajets, intempéries, absences. */
  paidHours: number;
  /** Celles qui sont imputables à un ouvrage — voir `isProjectCost`. */
  chargedHours: number;

  /**
   * Ce qu'il coûte : **toutes** ses heures au taux figé à la saisie, y compris
   * celles qu'aucun chantier n'absorbe. C'est la question posée — « ce qu'il
   * coûte » — et non la seule part que la comptabilité analytique impute.
   */
  cost: number;
  /**
   * Heures pointées sans taux horaire connu.
   *
   * Exposées plutôt que comptées à zéro, comme dans `WeeklyTally` et pour la
   * même raison : un salarié affiché à « 0 € de coût » sur des heures réelles
   * tromperait davantage qu'un total annoncé comme incomplet.
   */
  unpricedHours: number;

  /** Sa quote-part du chiffre d'affaires facturé des chantiers où il a pointé. */
  revenueShare: number;
  /** Sa quote-part des matières et sous-traitance de ces mêmes chantiers. */
  materialShare: number;

  /**
   * `revenueShare − materialShare − cost`. Ce que le patron appelle « ce qu'il
   * rapporte ».
   *
   * `null` quand aucun des chantiers où il a pointé n'est encore facturé :
   * afficher « −3 550 € » à propos d'un compagnon dont le chantier est en cours
   * et pas encore soldé le désignerait comme un poids mort alors qu'il
   * travaille.
   */
  contribution: number | null;

  /** Chantiers où il a pointé et dont rien n'est encore facturé. */
  unbilledProjectCount: number;

  /**
   * Heures imputables **rattachées à aucun chantier**.
   *
   * Ajouté le 10 septembre, après avoir ouvert l'écran : chez la première
   * entreprise regardée, les 130 heures du registre portaient toutes un
   * `project_id` nul. Le tableau affichait donc un mur de tirets sous un coût
   * bien réel, ce qui se lit « mes salariés me coûtent 3 000 € et ne
   * rapportent rien » — la conclusion exactement inverse de la vérité, qui est
   * qu'on ne sait pas à quel ouvrage rattacher leur travail.
   *
   * Le calcul était juste ; c'est l'écran qui se taisait. Un chiffre qu'on ne
   * peut pas produire doit dire pourquoi, sinon il ment par omission.
   */
  unassignedHours: number;
}

/**
 * Un chantier, réduit à ce dont la répartition a besoin.
 *
 * Les trois montants viennent des mêmes colonnes que la fiche chantier :
 * `invoicedHt` de `computeInvoicedHt`, `spentHt` et `labourCostHt` des colonnes
 * tenues par `recalc_project_costs`. Rien n'est recalculé ici.
 */
export interface ProjectLedger {
  projectId: string;
  /** Facturé hors taxes. `null` tant que rien n'est émis. */
  invoicedHt: number | null;
  /** Dépensé : matières, sous-traitance **et** main-d'œuvre. */
  spentHt: number;
  /** La part main-d'œuvre du dépensé. */
  labourCostHt: number;
}

/** Les heures d'un salarié sur un chantier, telles que le registre les tient. */
export interface EmployeeHours {
  employeeId: string;
  /** `null` pour une heure qui n'est rattachée à aucun ouvrage. */
  projectId: string | null;
  hours: number;
  /** Vrai pour les natures imputables à un ouvrage — `isProjectCost`. */
  charged: boolean;
  /** Figé à la saisie. `null` quand le taux n'était pas renseigné. */
  hourlyCost: number | null;
  /** Surcoût de majoration, calculé en base. Voir `TimeEntry.overtimePremium`. */
  overtimePremium: number;
}

/**
 * Répartit la marge des chantiers entre les salariés qui y ont pointé.
 *
 * Une propriété tient l'ensemble et mérite d'être énoncée : sur un chantier
 * donné, **la somme des `revenueShare − materialShare` de tous les salariés
 * égale le facturé moins les matières**, au centime. La répartition ne crée ni
 * ne détruit d'argent ; elle dit seulement à qui il est allé.
 *
 * L'écart entre la somme des contributions et la somme des marges de chantier
 * est donc exactement le **coût des heures payées qu'aucun ouvrage n'absorbe**
 * — trajets, intempéries, congés, formation. Ce n'est pas une erreur du calcul,
 * c'est un chiffre que le patron a intérêt à voir : c'est ce que son entreprise
 * paie sans le facturer à personne.
 */
export function computeEmployeeProfitability(
  employees: readonly Employee[],
  hours: readonly EmployeeHours[],
  projects: readonly ProjectLedger[],
): EmployeeProfitability[] {
  const ledger = new Map(projects.map((p) => [p.projectId, p]));

  // Total des heures imputables par chantier : c'est le dénominateur du
  // prorata. Calculé sur les mêmes lignes que les numérateurs, sans quoi les
  // parts ne sommeraient pas à un.
  const heuresParChantier = new Map<string, number>();
  for (const h of hours) {
    if (!h.charged || h.projectId == null || !ledger.has(h.projectId)) continue;
    heuresParChantier.set(h.projectId, (heuresParChantier.get(h.projectId) ?? 0) + h.hours);
  }

  return employees.map<EmployeeProfitability>((employee) => {
    const siennes = hours.filter((h) => h.employeeId === employee.id);

    let paidHours = 0;
    let chargedHours = 0;
    let cost = 0;
    let unpricedHours = 0;
    let revenueShare = 0;
    let materialShare = 0;
    let unassignedHours = 0;
    const chantiers = new Set<string>();
    const nonFactures = new Set<string>();

    for (const h of siennes) {
      paidHours += h.hours;
      if (h.hourlyCost == null) unpricedHours += h.hours;
      // La majoration fait partie du coût : c'est la même somme que celle du
      // chantier (`recalc_project_costs`), sans quoi le salarié paraîtrait
      // coûter moins que les chantiers qu'il a faits.
      else cost += h.hours * h.hourlyCost + (h.overtimePremium ?? 0);

      if (!h.charged) continue;
      chargedHours += h.hours;

      const projet = h.projectId == null ? undefined : ledger.get(h.projectId);
      if (!projet) {
        // Pointée sans chantier, ou sur un chantier que le dépôt n'a pas
        // rendu. Dans les deux cas rien ne peut lui être attribué, et c'est
        // cette absence-là qu'il faut annoncer plutôt que la laisser passer
        // pour une absence de travail.
        unassignedHours += h.hours;
        continue;
      }
      chantiers.add(projet.projectId);

      if (projet.invoicedHt == null) {
        nonFactures.add(projet.projectId);
        continue;
      }

      const total = heuresParChantier.get(projet.projectId) ?? 0;
      if (total <= 0) continue;
      const part = h.hours / total;
      // Les matières sont le dépensé moins la main-d'œuvre. Borné à zéro : un
      // chantier dont la main-d'œuvre dépasserait le dépensé — arrondi, ou
      // pointage saisi avant que la facture de matériaux ne remonte —
      // produirait sinon une matière négative, donc une production gonflée.
      const matieres = Math.max(0, projet.spentHt - projet.labourCostHt);
      revenueShare += projet.invoicedHt * part;
      materialShare += matieres * part;
    }

    const factures = chantiers.size - nonFactures.size;

    return {
      employeeId: employee.id,
      employeeName: employeeDisplayName(employee),
      isActive: employee.isActive,
      paidHours: round2(paidHours),
      chargedHours: round2(chargedHours),
      cost: round2(cost),
      unpricedHours: round2(unpricedHours),
      revenueShare: round2(revenueShare),
      materialShare: round2(materialShare),
      // `null` et non zéro tant qu'aucun chantier facturé ne le concerne :
      // sinon la contribution vaudrait « moins son coût », ce qui désignerait
      // en rouge un compagnon dont le chantier est simplement en cours.
      contribution: factures > 0 ? round2(revenueShare - materialShare - cost) : null,
      unbilledProjectCount: nonFactures.size,
      unassignedHours: round2(unassignedHours),
    };
  });
}

/**
 * Le coût des heures qu'aucun ouvrage n'absorbe.
 *
 * Trajets, intempéries, congés, formation : payés, jamais facturés. Le chiffre
 * n'existait nulle part, et c'est lui qui explique l'écart entre la somme des
 * contributions et la somme des marges de chantier — donc celui qu'il faut
 * écrire sous le tableau plutôt que laisser deviner.
 */
export function unbilledLabourCost(rows: readonly EmployeeProfitability[]): number {
  return round2(
    rows.reduce((s, r) => {
      // Au prorata des heures : le taux d'un salarié étant unique, la part non
      // imputable de son coût est la part non imputable de ses heures.
      const part = r.paidHours > 0 ? (r.paidHours - r.chargedHours) / r.paidHours : 0;
      return s + r.cost * part;
    }, 0),
  );
}
